/**
 * Answering a generative UI block away from the desktop (workbench D414 `ui.press`), for the console and the phone,
 * DOM-free. The workbench takes a press on a block exactly as its window's `ui-block-event`: the block's own action
 * object (only `do: "send"` is accepted from the web and the phone), the fields' values, once. It checks the action
 * against the stored block and refuses anything else in a sentence (-32003, `data.error: "ui_press_refused"`).
 *
 *  - {@link uiPressParams}: the call's params (the caller adds `conversationId` and `via`).
 *  - {@link pressableBlock}: whether a block can be answered from here at all ({@link isRemotePress}: which presses go).
 *  - {@link pressProblem}: what is still missing before a press can go (a required field), in a sentence.
 *  - {@link UI_PRESS_UNSUPPORTED}: a workbench before D414 does not know the method (-32601): blocks stay read only.
 */
import { missingRequired } from "./uiSpecModel.js";
/** `data.error` on a refused press. */
export const UI_PRESS_REFUSED = "ui_press_refused";
/** JSON-RPC method not found: the workbench predates `ui.press`. */
export const UI_PRESS_UNSUPPORTED = -32601;
/** `ui.press`'s params without the conversation and `via`. */
export function uiPressParams(messageId, blockId, action, values) {
    return { messageId, blockId, action, ...(values && Object.keys(values).length ? { values } : {}) };
}
/** A press the web and the phone may send: the block's "send" actions only. */
export function isRemotePress(action) {
    return action?.do === "send";
}
/** The sentence for a press that cannot go yet: a required field with no value. */
export function pressProblem(spec, values) {
    const missing = missingRequired(spec, values);
    if (!missing.length)
        return undefined;
    return missing.length === 1 ? `Fill in ${missing[0]} first.` : `Fill in ${missing.slice(0, -1).join(", ")} and ${missing.at(-1)} first.`;
}
/**
 * Whether a block can be answered from here: a block with an id, not yet answered, not an app proposal, in a
 * conversation, and with at least one "send" action the workbench would take (a button's, or a table's row action).
 */
export function pressableBlock(block, spec, conversationId) {
    if (!block.id || block.answered || block.proposal || !spec || !conversationId)
        return false;
    let any = false;
    const visit = (n) => {
        if (any || !n || typeof n !== "object")
            return;
        const node = n;
        if (node.type === "button" && isRemotePress(node.action))
            any = true;
        if (node.type === "table" && (node.rowActions ?? []).some((r) => isRemotePress(r.action)))
            any = true;
        for (const b of node.buttons ?? [])
            visit(b);
        for (const c of node.children ?? [])
            visit(c);
        for (const p of node.panels ?? [])
            for (const c of p.children ?? [])
                visit(c);
    };
    visit(spec.root);
    return any;
}
/** What a block's foot says while it can be answered here, after a press went, and when it cannot be answered here. */
export const PRESS_WORDS = {
    open: "Answer here: your choice goes to the agent.",
    sending: "Sending your answer…",
    sent: "Sent. The agent has your answer.",
    elsewhere: (whose) => `Answered on ${whose}: the answer shows here once it is given.`,
    older: (whose) => `Answer this on ${whose}. Once AI Workbench is updated, you can answer here too.`,
    otherAction: "This button works only on the workbench.",
};
//# sourceMappingURL=uiPress.js.map